import { DatePipe } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { isNullOrUndefined } from 'is-what';
import type { Subscription } from 'rxjs';
import type { ListCommittedMegaResponse } from '../../mega-exchange/services/interfaces/list-committed-mega-response';
import type { RetrieveMegaResponse } from '../../mega-exchange/services/interfaces/retrieve-mega-response';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { DownloadXlsxService } from '../../shared/services/download-xlsx.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { UserService } from '../../shared/services/user.service';
import { CommittedMegaTableComponent } from '../committed-mega-table/committed-mega-table.component';
import type { VolumeSummaryMegaResponse } from '../services/interfaces/volume-summary-mega-response';
import type { VolumeSummaryRequest } from '../services/interfaces/volume-summary-request';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';

@Component({
  standalone: false,
  selector: 'invd-committed-mega',
  templateUrl: './committed-mega.component.html',
  styleUrls: ['./committed-mega.component.scss'],
})
export class CommittedMegaComponent implements OnInit, OnDestroy {
  @ViewChild(CommittedMegaTableComponent, { static: true })
  public committedMegaTableComponent: CommittedMegaTableComponent;
  public displayPagination = false;

  private committedMegasRes: any;
  private volumeSummaryRes: any;
  private retrieveMegasRes: any;
  protected singleFamilyFixedMega = '';
  protected singleFamilyARMMega = '';
  protected multiFamilyFixedMega = '';
  protected multiFamilyARMMega = '';
  public showSFFixedMega = true;
  public showSFARMMega = false;
  public showMFFixedMega = false;
  public showMFARMMega = false;
  public showMessage = false;
  protected SFFixedMegaList: any;
  protected SFARMMegaList: any;
  protected MFFixedMegaList: any;
  protected MFARMMegaList: any;
  protected errorMessage: string;
  private errorSFFixedMessage: string;
  private errorSFARMMessage: string;
  private errorMFFixedMessage: string;
  private errorMFARMMessage: string;
  public displayInValidFilePopup: boolean;
  public displayInValidResponseFilePopup = false;
  public displayValidFilePopup: boolean;
  private counter = 0;
  public totalMFFixedMega: number;
  public totalMFARMMega: number;
  public totalSFFixedMega: number;
  public totalSFARMMega: number;
  public hasCreateOrEditMegaAccessFlag: boolean;
  public hasCollateralUploadAccessFlag: boolean;
  public hasListSettledAndCommittedAccessFlag: boolean;
  public totalCommittedVolumeSFFIXMEGA = 0;
  public totalCommittedVolumeSFARMMEGA = 0;
  public totalCommittedVolumeMFFIXMEGA = 0;
  public totalCommittedVolumeMFARMMEGA = 0;

  public totalCommittedCurrentMonthSFFIXMEGA = 0;
  public totalCommittedCurrentMonthSFARMMEGA = 0;
  public totalCommittedCurrentMonthMFFIXMEGA = 0;
  public totalCommittedCurrentMonthMFARMMEGA = 0;
  public downloadXLSXResponse: any;
  public featuretoggle: string[] = [];
  public isInternalUser = false;
  public downloadPoolInfoTemplateLinkClicked = true;
  public routerSubscription: Subscription;
  public listCommittedMegaLinkClicked = true;
  public monthlyVolumeSummaryLinkClicked = true;
  public downloadCollateralPoolLinkClicked = true;
  public poolNumber = '';

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private megaService = inject(MegasService);
  private userService = inject(UserService);
  private passDataService = inject(PassDataService);
  private downloadXlsx = inject(DownloadXlsxService);
  private cdr = inject(ChangeDetectorRef);
  public datepipe = new DatePipe('en-US');

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.hasCreateOrEditMegaAccessFlag = this.userService.hasCreateOrEditMegaAccessFlag;
    this.hasCollateralUploadAccessFlag = this.userService.hasCollateralUploadAccessFlag;
    this.hasListSettledAndCommittedAccessFlag =
      this.userService.hasListSettledAndCommittedAccessFlag;
    this.featuretoggle = this.userService.toggleFeatures;
    this.isInternalUser = this.userService.isInternalUser;
    this.totalMFARMMega = 0;
    this.totalMFFixedMega = 0;
    this.totalSFFixedMega = 0;
    this.totalSFARMMega = 0;

    this.downloadPoolInfoTemplateLinkClicked = true;
    this.showSFFixedMega = true;
    this.getListCommittedMega();
    this.getMonthlyVolumeSummary();
    this.routerSubscription = this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationStart && evt.url === '/mega-trade/committed-mega') {
        this.committedMegaTableComponent.presentSortedColumn = '';
        this.committedMegaTableComponent.presentSortedColumnIsAscending = false;
        this.getListCommittedMega();
        this.getMonthlyVolumeSummary();
        window.scrollTo(0, 0);
      }
    });
  }

  ngOnDestroy() {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public getMonthlyVolumeSummary(): void {
    const volumeSummary = {} as VolumeSummaryRequest;
    volumeSummary.currentMonthOffsetStart = 0;
    volumeSummary.currentMonthOffsetEnd = 0;
    if (this.monthlyVolumeSummaryLinkClicked) {
      this.megaService.monthlyVolumeSummary(volumeSummary).subscribe({
        next: (data) => this.handleMonthlyVolumeSummarySuccess(data as VolumeSummaryMegaResponse),
        error: (error) => this.handleAWSMonthlyVolumeSummaryMegaServiceFail(error),
      });
    }
    this.monthlyVolumeSummaryLinkClicked = false;
  }

  private handleMonthlyVolumeSummarySuccess(volumeSummary: VolumeSummaryMegaResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.volumeSummaryRes = volumeSummary && volumeSummary.monthlyVolumeSummary[0].megaTypeDetail;

    this.volumeSummaryRes.forEach((item) => {
      if (item.megaType != null) {
        if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX) {
          this.totalCommittedCurrentMonthSFFIXMEGA = item.volumeInfo.committed;
        } else if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM) {
          this.totalCommittedCurrentMonthSFARMMEGA = item.volumeInfo.committed;
        } else if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX) {
          this.totalCommittedCurrentMonthMFFIXMEGA = item.volumeInfo.committed;
        } else {
          this.totalCommittedCurrentMonthMFARMMEGA = item.volumeInfo.committed;
        }
      }
    });

    this.monthlyVolumeSummaryLinkClicked = true;
    this.cdr.markForCheck();
  }

  private handleAWSMonthlyVolumeSummaryMegaServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.monthlyVolumeSummaryLinkClicked = true;
    this.cdr.markForCheck();
  }

  public getListCommittedMega(): void {
    if (this.listCommittedMegaLinkClicked) {
      this.megaService.listCommittedMega().subscribe({
        next: (data) =>
          this.handelListCommittedMegaServiceSuccess(data as ListCommittedMegaResponse),
        error: (error) => this.handleAWSListCommittedMegaServiceFail(error),
      });
    }
    this.listCommittedMegaLinkClicked = false;
  }

  private handelListCommittedMegaServiceSuccess(listCommitted: ListCommittedMegaResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);

    this.committedMegasRes = listCommitted && listCommitted.committedMegas;

    this.committedMegasRes.forEach((item) => {
      if (item.megaType != null) {
        if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX) {
          this.singleFamilyFixedMega = 'SFFIXMEGA';
          if (item.megaList !== null) {
            this.SFFixedMegaList = item.megaList;
            this.totalCommittedVolumeSFFIXMEGA = item.totalVolume;
            this.totalSFFixedMega = this.SFFixedMegaList.length;
            if (this.SFFixedMegaList.length > 50) {
              this.displayPagination = true;
            } else {
              this.displayPagination = false;
            }
          } else {
            this.SFFixedMegaList = null;
            this.errorSFFixedMessage = item.errorMessage;
            this.errorMessage = this.errorSFFixedMessage;
            this.showMessage = true;
          }
        } else if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM) {
          this.singleFamilyARMMega = 'SFARMMEGA';
          if (item.megaList !== null) {
            this.SFARMMegaList = item.megaList;
            this.totalCommittedVolumeSFARMMEGA = item.totalVolume;
            this.totalSFARMMega = this.SFARMMegaList.length;
          } else {
            this.SFARMMegaList = null;
            this.errorSFARMMessage = item.errorMessage;
          }
        } else if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX) {
          this.multiFamilyFixedMega = 'MFFIXMEGA';
          if (item.megaList !== null) {
            this.MFFixedMegaList = item.megaList;
            this.totalCommittedVolumeMFFIXMEGA = item.totalVolume;
            this.totalMFFixedMega = this.MFFixedMegaList.length;
          } else {
            this.MFFixedMegaList = null;
            this.errorMFFixedMessage = item.errorMessage;
          }
        } else if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM) {
          this.multiFamilyARMMega = 'MFARMMEGA';
          if (item.megaList !== null) {
            this.MFARMMegaList = item.megaList;
            this.totalCommittedVolumeMFARMMEGA = item.totalVolume;
            this.totalMFARMMega = this.MFARMMegaList.length;
          } else {
            this.MFARMMegaList = null;
            this.errorMFARMMessage = item.errorMessage;
          }
        }
      }
    });
    this.listCommittedMegaLinkClicked = true;
    this.showMegaType('SFFIXMEGA');
    this.cdr.markForCheck();
  }

  private handleAWSListCommittedMegaServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.listCommittedMegaLinkClicked = true;
    this.cdr.markForCheck();
  }

  protected navigatePageTo(pageURL) {
    if (this.showSFFixedMega) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX,
      );
    } else if (this.showSFARMMega) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM,
      );
    } else if (this.showMFFixedMega) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX,
      );
    } else if (this.showMFARMMega) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM,
      );
    }
    this.router.navigate([pageURL]);
  }

  public showMegaType(type) {
    if (type === 'SFFIXMEGA') {
      this.showSFFixedMega = true;
      this.showMFFixedMega = false;
      this.showSFARMMega = false;
      this.showMFARMMega = false;
      if (this.SFFixedMegaList !== null) {
        this.showMessage = false;
        if (!isNullOrUndefined(this.committedMegaTableComponent)) {
          this.committedMegaTableComponent.config = {
            id: 'committed-mega-pagination',
            itemsPerPage: 50,
            currentPage: 1,
          };
        }

        if (this.SFFixedMegaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.showMessage = true;
        this.errorMessage = this.errorSFFixedMessage;
      }
    } else if (type === 'SFARMMEGA') {
      this.showSFFixedMega = false;
      this.showMFFixedMega = false;
      this.showSFARMMega = true;
      this.showMFARMMega = false;
      if (this.SFARMMegaList !== null) {
        this.showMessage = false;
        this.committedMegaTableComponent.config = {
          id: 'committed-mega-pagination',
          itemsPerPage: 50,
          currentPage: 1,
        };
        if (this.SFARMMegaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.showMessage = true;
        this.errorMessage = this.errorSFARMMessage;
      }
    } else if (type === 'MFFIXMEGA') {
      this.showSFFixedMega = false;
      this.showMFFixedMega = true;
      this.showSFARMMega = false;
      this.showMFARMMega = false;
      if (this.MFFixedMegaList !== null) {
        this.showMessage = false;
        this.committedMegaTableComponent.config = {
          id: 'committed-mega-pagination',
          itemsPerPage: 50,
          currentPage: 1,
        };
        if (this.MFFixedMegaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.showMessage = true;
        this.errorMessage = this.errorMFFixedMessage;
      }
    } else if (type === 'MFARMMEGA') {
      this.showSFFixedMega = false;
      this.showMFFixedMega = false;
      this.showSFARMMega = false;
      this.showMFARMMega = true;
      if (this.MFARMMegaList !== null) {
        this.showMessage = false;
        this.committedMegaTableComponent.config = {
          id: 'committed-mega-pagination',
          itemsPerPage: 50,
          currentPage: 1,
        };
        if (this.MFARMMegaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.showMessage = true;
        this.errorMessage = this.errorMFARMMessage;
      }
    }
  }

  public editMegaClick(committedMegaObj: any): void {
    if (committedMegaObj && committedMegaObj.editMegaClickedCount === 1) {
      this.retrieveMega(committedMegaObj.poolNumber);
    }

    return null;
  }

  private retrieveMega(poolNumber): void {
    this.megaService.retrieveMega({ poolNumber }).subscribe({
      next: (data) => this.handelRetrieveMegaServiceSuccess(data as RetrieveMegaResponse),
      error: (error) => this.handleAWSRetrieveMegaServiceFail(error),
    });
  }

  private handelRetrieveMegaServiceSuccess(retrieveMegaRes: RetrieveMegaResponse) {
    this.fmLoadingPageService.stopLoading();
    this.passDataService.setRetrieveMegaResponse(retrieveMegaRes);
    this.router.navigate(['mega-trade/enter-details']);
    return;
  }

  private handleAWSRetrieveMegaServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public onFileUpload(fileInput: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const fi = fileInput;
    let committedDate = '';
    let tradeSettlementDate = '';

    if (fi && fi.file && fi.file.name) {
      let fileName = fi.file.name;
      fileName = fileName.replace(/[^a-zA-Z 0-9 . - _]+/g, '');
      let megaType = '';

      if (fi.committedMega.sfFixedCoupon !== null && this.showSFFixedMega === true) {
        megaType = 'SFFIXMEGA';
      } else if (
        (fi.committedMega.mfHighCoupon !== null || fi.committedMega.mfLowCoupon !== null) &&
        this.showMFFixedMega === true
      ) {
        megaType = 'MFFIXMEGA';
      } else if (fi.committedMega.mfHighCoupon !== null && this.showSFARMMega === true) {
        megaType = 'SFARMMEGA';
      } else {
        megaType = 'MFARMMEGA';
      }
      if (fi.committedMega && fi.committedMega.committedDate !== '') {
        committedDate = this.datepipe.transform(fi.committedMega.committedDate, 'MM/dd/yyyy');
      }

      if (fi.committedMega && fi.committedMega.tradeSettlementDate !== '') {
        tradeSettlementDate = this.datepipe.transform(
          fi.committedMega.tradeSettlementDate,
          'MM/dd/yyyy',
        );
      }

      const request: any = new Object();
      request.cusip = fi.committedMega.cusip;
      request.feeDollars =
        fi.committedMega && fi.committedMega.feeDollars
          ? fi.committedMega.feeDollars.toString()
          : '';
      request.isEditable = fi.committedMega.isEditable;
      request.committedDate = committedDate ? committedDate : '';
      request.mfHighCoupon = fi.committedMega.mfHighCoupon
        ? fi.committedMega.mfHighCoupon.toString()
        : '';
      request.mfLowCoupon = fi.committedMega.mfLowCoupon
        ? fi.committedMega.mfLowCoupon.toString()
        : '';
      request.poolNumber = fi.committedMega.poolNumber ? fi.committedMega.poolNumber : '';
      request.prefixCode = fi.committedMega.prefixCode ? fi.committedMega.prefixCode : '';
      request.sfARMSubType =
        fi.committedMega && fi.committedMega.sfARMSubType ? fi.committedMega.sfARMSubType : '';
      request.sfFixedCoupon = fi.committedMega.sfFixedCoupon
        ? fi.committedMega.sfFixedCoupon.toString()
        : '';
      request.tradeAmount = fi.committedMega.tradeAmount
        ? fi.committedMega.tradeAmount.toString()
        : '';
      request.feeTicks = fi.committedMega.feeTicks ? fi.committedMega.feeTicks.toString() : '';
      request.dealerOrgName = this.userService.getDealerOrgName();
      request.emailAddress = this.userService.getUserEmailAddress();
      request.megaType = megaType;
      request.tradeSettlementDate = tradeSettlementDate ? tradeSettlementDate : '';
      request.userName = this.userService.getUserName();
      request.fileName = fi.committedMega.poolNumber + '-' + fileName;
      request.originalFileName = fi.file.name;
      request.contact =
        this.userService.getUserFirstName() + ' ' + this.userService.getUserLastName();
      request.fileStatus = 'initiated';

      const allowedFiles = ['.xls', '.xlsx', '.csv'];
      const regex = new RegExp('([a-zA-Z0-9 s_\\.-:])+(' + allowedFiles.join('|') + ')$');
      if (regex.test(fileName.toLowerCase())) {
        this.megaService.uploadCollateralEndpoint(request).subscribe({
          next: (data) => this.handelUploadCollateralSuccess(data, fileInput),
          error: (error) => this.handelUploadCollateralFail(error),
        });
      } else {
        this.fmLoadingPageService.isLoaderRequesting.set(false);
        this.displayInValidFilePopup = true;
        this.displayInValidResponseFilePopup = false;
      }
    }
  }

  public callFileUpload(fileInput: any, url: string, kmsId: string) {
    const fi = fileInput;
    if (fi.file.name) {
      const fileToUpload = fi;

      const httpOptions = {
        headers: new HttpHeaders({
          'x-amz-server-side-encryption': 'aws:kms',
          'x-amz-server-side-encryption-aws-kms-key-id': kmsId,
        }),
      };

      this.megaService.uploadCollateral(fileToUpload.file, httpOptions, url).subscribe({
        next: (data) => this.handelUploadSuccess(data),
        error: (error) => this.handleUploadFail(error),
      });
    }
  }

  private handelUploadSuccess(_response: any) {
    this.displayValidFilePopup = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  private handleUploadFail(_error: any): void {
    this.displayInValidFilePopup = true;
    this.displayInValidResponseFilePopup = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  private handelUploadCollateralSuccess(response: any, fileInput: any) {
    const url = response && response[0].url;
    const kmsId = response && response[0].kmsId;
    this.callFileUpload(fileInput, url, kmsId);
  }

  private handelUploadCollateralFail(_error: any): void {
    this.displayInValidFilePopup = true;
    this.displayInValidResponseFilePopup = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public displayValidFileStatusPopup(): void {
    this.displayValidFilePopup = false;
    if (this.counter !== 0) {
      return;
    }
    this.counter = 1;
    // to auto refresh the committed mega page after 20 sec.
    setTimeout(() => {
      this.fmLoadingPageService.isLoaderRequesting.set(true);
      this.getListCommittedMega();
      this.counter = 0;
    }, 20000);
  }

  public displayInValidFileStatusPopup(): void {
    this.displayInValidFilePopup = false;
    this.getListCommittedMega();
  }

  public downloadPoolInfoTemplate(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    if (this.downloadPoolInfoTemplateLinkClicked) {
      this.megaService.downloadXLSXPoolInfoTemplate().subscribe({
        next: (data) => this.handleDownloadXLSXServiceSuccess(data),
        error: (error) => this.handleAWSownloadXLSXServiceFail(error),
      });
    }

    this.downloadPoolInfoTemplateLinkClicked = false;
  }

  public handleDownloadXLSXServiceSuccess(downloadXLSXRes) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadXLSXResponse = downloadXLSXRes;
    let data = this.downloadXLSXResponse;
    const fileName = 'Fannie Mae Pool Information Template.xlsx';
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }

    this.downloadPoolInfoTemplateLinkClicked = true;
    this.cdr.markForCheck();
  }

  public handleAWSownloadXLSXServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadPoolInfoTemplateLinkClicked = true;
    this.cdr.markForCheck();
  }

  downloadXLSX(data) {
    let fileName = '';

    if (!data) {
      return;
    }
    fileName = this.poolNumber + '_collateral_confirmed';

    this.downloadXlsx.generateMegaExcel(data, fileName);
    // this.fmJsonToExcelService.exportAsXLSX(jsonText, this.poolNumber + "_collateral_confirmed");
  }

  public downloadCollateralPoolFromParent(committedMegaDownloadPoolObj: any): void {
    this.poolNumber = committedMegaDownloadPoolObj.poolNumber;

    if (this.downloadCollateralPoolLinkClicked) {
      this.megaService.downloadCollateralPool(this.poolNumber).subscribe({
        next: (data) => this.handledownloadCollateralPoolFromParentSuccess(data),
        error: (error) => this.handleAWSdownloadCollateralPoolFromParentServiceFail(error),
      });
    }
    this.downloadCollateralPoolLinkClicked = false;
  }

  private handledownloadCollateralPoolFromParentSuccess(response) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadCollateralPoolLinkClicked = true;
    this.downloadXLSX(response);
    this.cdr.markForCheck();
  }

  private handleAWSdownloadCollateralPoolFromParentServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadCollateralPoolLinkClicked = true;
    this.cdr.markForCheck();
  }

  public refreshList(): void {
    this.getListCommittedMega();
  }
}
